import { useCallback, useEffect, useRef } from 'react';

import { useMytaverse } from '@providers/MytaverseProvider';
import { useMytaverseEvent } from '@core/dashboard/providers';
import { useChatState } from '@core/chat/context';
import { useLogout } from '@components/ConfirmLogoutModal/hooks';
import { sendCustomSentryBreadcrumb } from '@helpers/sentry';
import { MytaverseLogger } from '@helpers/logger';
import { default as mytaverseLogger } from '@providers/MytaverseProvider/logger';
import ROUTES from '@constants/routes';
import { SessionStorage } from '@constants/storage';
import { setSessionStorageValue } from '@helpers/sessionStorage';
import {
  LastWebsocketJsonMessageType,
  WebsocketAction,
} from '@interfaces/webSocketConnectionInfo';

/** Time given to the browser to flush CLOSE_GAME before logout. */
const WEBSOCKET_FLUSH_DELAY_MS = 300;

/** Cap on the free-form origin string UE sends, before it reaches the logs. */
const MAX_REASON_LENGTH = 200;

/** Upper bound on the teardown before the page is left regardless. */
const LOGOUT_DEADLINE_MS = 5000;

/**
 * MV-1718 — logout initiated from inside Unreal Engine.
 *
 * UE sends `LOGOUT_REQUEST` on its own websocket connection (a button in the
 * game UI) and the backend relays it to this session's WEBAPP connections. The
 * hub answers with `CLOSE_GAME` so UE quits and the stream session ends, then
 * unwinds the session exactly like the in-hub logout button (stop sharing →
 * clear event and chat state → drop the tokens → redirect to /login).
 *
 * `CLOSE_GAME` goes first because the logout tears down the socket it travels
 * on; if the logout then fails, it is forced through anyway, since the game is
 * already gone by that point.
 *
 * @param lastJsonMessage - Latest message off the dashboard websocket; anything
 * other than `LOGOUT_REQUEST`, or a stale stream session's key, is ignored.
 */
export const useHandleLogoutRequestMsg = (
  lastJsonMessage: LastWebsocketJsonMessageType,
) => {
  const { sendJSONMessageToWebSocket, isDebugMode, logoutUser } =
    useMytaverse();
  const { cleanStates, gameliftStreamsSessionKey } = useMytaverseEvent();
  const { resetState } = useChatState();

  // UE may send the message more than once (double click, retry after no
  // visible change): the first one owns the teardown.
  const logoutStartedRef = useRef(false);

  const handleOnConfirmLogout = useCallback(() => {
    cleanStates();
    resetState();
  }, [cleanStates, resetState]);

  const logout = useLogout({ onConfirm: handleOnConfirmLogout });

  useEffect(() => {
    if (!lastJsonMessage) {
      return;
    }

    const { action, sessionKey, reason } = lastJsonMessage;

    if (action !== WebsocketAction.LogoutRequest) {
      return;
    }

    if (
      sessionKey &&
      sessionKey !== gameliftStreamsSessionKey &&
      !isDebugMode
    ) {
      mytaverseLogger.debug(
        `Skip handle WS message for sessionKey: ${sessionKey}, expected sessionKey: ${gameliftStreamsSessionKey}`,
      );
      return;
    }

    if (logoutStartedRef.current) {
      MytaverseLogger.log('Logout request from UE ignored, logout in progress');
      return;
    }

    logoutStartedRef.current = true;

    const message = `Logout request from UE${
      reason ? ` (reason: ${reason.slice(0, MAX_REASON_LENGTH)})` : ''
    }`;
    MytaverseLogger.log(message);
    sendCustomSentryBreadcrumb({ message });

    let left = false;

    // A full navigation, not a router push: closing the websocket is what lets
    // the backend's fallback see a session with no WEBAPP connection left and
    // terminate the abandoned stream. It is the guarantee of the whole flow, so
    // it runs on a deadline rather than waiting on a teardown that may hang.
    const leave = () => {
      if (left) {
        return;
      }

      left = true;
      // The navigation wipes React state, so the notice is handed to the next
      // page through session storage instead.
      setSessionStorageValue(SessionStorage.LeftExperienceNotice, true);
      window.location.assign(ROUTES.LOGIN);
    };

    const deadline = setTimeout(leave, LOGOUT_DEADLINE_MS);

    const runLogout = async () => {
      sendJSONMessageToWebSocket({ action: WebsocketAction.CloseGame });

      await new Promise((resolve) =>
        setTimeout(resolve, WEBSOCKET_FLUSH_DELAY_MS),
      );

      await logout();
    };

    runLogout()
      // A partial logout is worse than none: the game is already closing, so
      // drop the session even if the media teardown inside logout() rejects.
      .catch(async (error) => {
        MytaverseLogger.error(
          `Logout request from UE failed, forcing logout: ${
            error?.message ?? error
          }`,
        );
        handleOnConfirmLogout();
        await logoutUser();
      })
      .finally(() => {
        clearTimeout(deadline);
        leave();
      });

    // Deliberately no cleanup: CLOSE_GAME kills the stream, which re-renders
    // this tree, and cancelling the timers there would strand the teardown
    // half-done. Re-entry is already blocked by logoutStartedRef.
  }, [
    lastJsonMessage,
    gameliftStreamsSessionKey,
    isDebugMode,
    sendJSONMessageToWebSocket,
    logout,
    logoutUser,
    handleOnConfirmLogout,
  ]);
};
